import { Component, ViewChild, ElementRef, OnInit, OnDestroy, Inject, Optional, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, finalize } from 'rxjs';

import { FormGroup, FormControl, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatDialog, MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';

import { ProductsService, ProductFilter } from '@app/sales/products/products.service';
import { MatIconModule } from '@angular/material/icon';
import { CommonService } from '@app/common/common.service';
import * as constants from '@app/shared/constants';
import * as common from '@app/shared/_models/common';
import { CufComponent } from '@app/shared/cuf/cuf.component';

import { MenuCommonComponent } from '../../shared/menu-common/menu-common.component';
import { SelectPackagingQty } from './select-packaging-qty/selectPackagingQty.component';
import { Product } from '@app/shared/_models/product';
import { Partner } from '@app/shared/_models/common';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SmartInputComponent } from '@app/shared/smart-input/smart-input.component';

@Component({
  selector: 'app-products-tpl',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss', '../../../theme/styles_color_theme.scss'],
  imports: [SmartInputComponent, MatTooltipModule, ReactiveFormsModule, CufComponent, MatProgressSpinnerModule, MenuCommonComponent, MatDialogModule, MatIconModule, RouterLink, SearchPartnerComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class ProductsComponent implements OnInit, OnDestroy {
  @ViewChild('productIdNav') productIdNavInput!: SmartInputComponent;
  @ViewChild('minPriceInput') minPriceInput!: ElementRef<HTMLInputElement>;
  @ViewChild('maxPriceInput') maxPriceInput!: ElementRef<HTMLInputElement>;

  readonly constants = constants;

  // En modale, la feuille remplit la surface Material et seule la liste défile (voir la
  // feuille) : la classe DOIT être portée par l'hôte, le composant n'a pas de racine à lui.
  @HostBinding('class.in-dialog') get hostInDialog(): boolean { return !!this.dialogRef; }

  // Produit à plat et modèle naissent du MÊME enregistrement (process_template_insert) : même fenêtre, seule la suite diffère.
  public readonly NEW_FLAT_HINT = 'Créer un PRODUIT à plat :\nun modèle et son unique produit, créés ensemble.\nOn arrive sur la fenêtre qui saisit les deux.';
  public readonly NEW_TPL_HINT = 'Créer un MODÈLE générique :\nun produit générique, sans produit spécifique.\nSes déclinaisons se déclarent ensuite par les attributs.';
  public readonly SOLD_HINT = 'Un clic fait tourner le filtre : Oui → Non → Tous.\nOui : les articles vendables seulement ; Non : les non vendables seulement ; Tous : sans filtre.';
  public readonly PURCHASED_HINT = 'Un clic fait tourner le filtre : Oui → Non → Tous.\nOui : les articles achetables seulement ; Non : les non achetables seulement ; Tous : sans filtre.';
  // En modale, les deux boutons sont de vrais liens : posés à l'init, jamais dans un getter
  // (le gabarit les relit à chaque cycle de détection).
  public newFlatProductHref = '';
  public newTemplateHref = '';

  // Nombre de modèles ramenés. Le catalogue se compte en dizaines de milliers d'articles :
  // la liste est toujours bornée, jamais d'« Illimité » ici.
  public readonly limitsValue: number[] = [30, 50, 100, 200, 500, 1000];
  // @HostListener('document:keyup.enter', ['$event'])  // Écoute toute la page : touche "Enter"
  // handleEnter(event: KeyboardEvent) {    // le problème c'est que on peut appuyer sur enter pour choisir un manuf dans l'autocomplete et cela déclence handleEnter et la recherche avant que le component search manuf ai update le manuf
  //     runInInjectionContext(this.injector, () => {
  //       afterNextRender(() => setTimeout( ()=>{ this.onSearch();  this.cdr.detectChanges()  ;}  , 5) );  // on laisse le temps à Angular de finir le rendu avant de lancer la recherche
  //     });
  //   }
  // [clé interne, libellé affiché, classes CSS, sens du tri courant, infobulle d'entête]
  public displayedColumnsInfo: [string, string, string, boolean?, string?][] = [
    ['is_sold', 'V.', 'cell_is_sold', undefined, 'Produit disponible à la vente'],
    ['is_purchased', 'A.', 'cell_is_purch', undefined, 'Produit commandable chez nos fournisseurs'],
    ['id', '#', 'cell_id', undefined, 'Gauche : id du modèle (générique)\nDroite : id du produit (spécifique)'],
    ['name', 'Nom', 'name-cell'],
    ['lnk_partner_manuf.name', 'Fab / Attr', 'manuf-cell', undefined, 'Fabricant du modèle ; sur une déclinaison, ses attributs'],
    ['baseuom/unitpriceuom', 'UVC | Unité', 'uom-cell', undefined, 'Unité de vente consommateur | unité de mesure du prix'],
    ['price_taxi', 'PV. TTC', 'price-cell'],
    ['refs', 'Ref Int | Four | Fab', 'refs-cell', undefined, 'Référence interne | fournisseur | fabricant'],
  ];

  public productsTpl: any[] = [];

  // Pistes en px calibrées sur le contenu réel ; seuls Nom et Fab sont élastiques (min 0) : la carte en overflow: clip ne masque jamais une colonne.
  // Calculées ici car les cufs déclarés en base ajoutent des colonnes (documentation.html#front.sales.selecteur-produits).
  private trackFor(col: [string, string, string, boolean?, string?]): string {
    switch (col[0]) {
      case 'is_sold':
      case 'is_purchased': return '26px'; // case de 16 px + « ↑ V. » trié en entête
      case 'id': return '140px'; // deux identifiants de 8 chiffres (max de la base : 20 115 213) côte à côte, en chasse fixe
      case 'name': return 'minmax(0, 1fr)';
      case 'lnk_partner_manuf.name': return 'minmax(0, 0.6fr)';
      case 'baseuom/unitpriceuom': return '126px';
      case 'price_taxi': return '86px';
      case 'refs': return '224px'; // « TEN-PP-S | 7322541140315 | 795628 » : une réf. fournisseur est souvent un EAN de 13 chiffres
      default: return '112px'; // colonne de champ libre (cuf)
    }
  }

  // Mémoïsé : le gabarit lit la valeur à chaque cycle de détection, et `displayedColumnsInfo`
  // ne bouge que deux fois (arrivée des cufs, puis chaque tri qui réécrit le tableau).
  private tracksSource: any[] | null = null;
  private tracksLength = 0;
  private tracksCache = '';

  get gridTemplateColumns(): string {
    if (this.tracksSource !== this.displayedColumnsInfo || this.tracksLength !== this.displayedColumnsInfo.length) {
      this.tracksSource = this.displayedColumnsInfo;
      this.tracksLength = this.displayedColumnsInfo.length;
      this.tracksCache = this.displayedColumnsInfo.map(col => this.trackFor(col)).join(' ');
    }
    return this.tracksCache;
  }

  // Produits AFFICHÉS : un modèle à déclinaison unique vaut un produit, un modèle générique
  // en vaut autant qu'il montre de déclinaisons. Mémoïsé sur la référence de productsTpl.
  private countSource: any[] | null = null;
  private countCache = 0;

  get productCount(): number {
    if (this.countSource !== this.productsTpl) {
      this.countSource = this.productsTpl;
      this.countCache = this.productsTpl.reduce((n, tpl) => n + (tpl?.lnk_products?.length ?? 0), 0);
    }
    return this.countCache;
  }

  // Infobulle d'entête : '' = aucune. Elle survit au tri (voir sortProductTpl).
  colHint(col: [string, string, string, boolean?, string?]): string {
    return col[4] ?? '';
  }

  // Les trois références sont du texte libre : « 987 | CHAMBORD+COLORIS | CHAMBORD+COLORIS »
  // demande 266 px là où la piste en fait 224. L'infobulle native rend la valeur entière.
  refsOf(product: any): string {
    return `Réf. interne : ${product?.partnbloc || '—'}\nRéf. fournisseur : ${product?.partnbsup || '—'}\nRéf. fabricant : ${product?.partnbman || '—'}`;
  }

  public formGroup!: FormGroup;
  public searchmanuf: Partner = new Partner(); // Manuf Search field
  // public searchlpp: any = "";	// lpp Search field
  public loadingProducts: boolean = false; // Loading spinner
  public searchDropped: boolean = false; // une saisie a coupé la recherche en vol : la liste vide ne veut pas dire « Aucun produit »
  public searchLaunched: boolean = false; // faux tant qu'aucune recherche n'est partie : la liste vide n'est pas encore « Aucun produit »
  private productsSearchSub?: Subscription; // LA recherche en vol (filtres ou id), coupée par la suivante ou par la moindre saisie
  private formChangesSub?: Subscription;
  private icufChangesSub?: Subscription; // icuf est remplacé à l'arrivée des cufs : l'écoute suit le nouveau groupe
  private cufsStructureSubsciption!: Subscription;
  public cufsStructure: common.Cuf[] = [];
  public icuf = new FormGroup({});

  public productIdHref: string | null = null; // href de l'icône lien, recalculé à chaque frappe
  private lastProductIdSearched: number | null = null; // id réellement affiché, null dès qu'une autre recherche prend la main
  private productIdNavClosing: boolean = false; // la modale part ou la fiche s'ouvre : on ignore le blur qui suit

  constructor(
    private productsService: ProductsService,
    public dialog: MatDialog,
    private commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    @Optional() public dialogRef: MatDialogRef<ProductsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
  ) {}

  ngOnInit() {
    this.newFlatProductHref = this.router.serializeUrl(this.router.createUrlTree(['/sales/product-tpl'], { queryParams: { kind: 'flat' } }));
    this.newTemplateHref = this.router.serializeUrl(this.router.createUrlTree(['/sales/product-tpl'], { queryParams: { kind: 'generic' } }));

    this.formGroup = this.fb.group({
      name: [''],
      ean: [''],
      refsup: [''],
      manuf: [null as number | null],
      minprice: [null as number | null],
      maxprice: [null as number | null],
      is_sold: [true as boolean | null], // trois états : true = oui, false = non, null = tous
      is_purchased: [null as boolean | null],
      limit: [constants.LIMIT_SELECTOR],
    });
    this.formChangesSub = this.formGroup.valueChanges.subscribe(() => this.dropPendingSearch());
    this.cufsStructureSubsciption = this.commonService.getCufs(constants.C_COMPONENT_PRODUCT).subscribe((cufsStructure: common.Cuf[]) => {
      for (const cufStructure of cufsStructure) {
        if (cufStructure?.data && cufStructure?.data?.products && cufStructure?.data?.products.length > 0) {
          for (const cufLabel of cufStructure?.data?.products) {
            this.displayedColumnsInfo.push(['lnk_cufs.' + cufStructure.id + '.' + cufLabel.name, cufLabel.label, cufLabel.classes]);
          }
        }
      }
      // console.log('this.displayedColumnsInfo',this.displayedColumnsInfo);
      const group: { [key: string]: FormControl } = {};
      cufsStructure.forEach(cuf => {
        if (cuf.id !== undefined) {
          if (cuf.data?.is_sortable && (cuf.data?.typ === 'INT' || cuf.data?.typ === 'FLOAT')) {
            group['gte_' + cuf.id] = new FormControl(null);
            group['lte_' + cuf.id] = new FormControl(null);
          } else {
            group[cuf.id] = new FormControl('');
          }
        }
      });
      this.icuf = new FormGroup(group);
      this.icufChangesSub?.unsubscribe();
      this.icufChangesSub = this.icuf.valueChanges.subscribe(() => this.dropPendingSearch());
      this.cufsStructure = cufsStructure;
      // console.log('this.cufsStructure',this.cufsStructure);
      // console.log('this.icuf',this.icuf);
      if (this.cufsStructureSubsciption) {
        this.cufsStructureSubsciption.unsubscribe();
      }
    });

    // Sans nom fourni par l'appelant, rien ne part : les premiers articles d'un catalogue de 200 000 n'apprennent rien.
    if (this.dialogData && this.dialogData.searchname) {
      this.formGroup.get('name')?.setValue(this.dialogData.searchname);
      this.onSearch();
    }
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => this.productIdNavInput?.focus());

    //     // FOR DEV
    // setTimeout(() => {
    //   // this.icuf.patchValue({'5': '1382541'})
    //   // this.icuf.markAsDirty();
    //   this.formGroup.get('name')?.setValue('tena');
    //   this.onSearch();
    // //  this.icuf.controls['5'].setValue('6319040'); // Set default value for id_cuf_5
    // } , 110); // Delay to ensure the input is rendered before focusing
  }

  ngOnDestroy(): void {
    this.productsSearchSub?.unsubscribe(); // modale fermée ou écran quitté : plus personne n'attend la réponse
    this.formChangesSub?.unsubscribe();
    this.icufChangesSub?.unsubscribe();
  }

  // Toute saisie dans la barre rend obsolète la recherche en vol : sa réponse passerait pour celle des critères affichés.
  dropPendingSearch(): void {
    if (!this.productsSearchSub || this.productsSearchSub.closed) return;
    this.productsSearchSub.unsubscribe(); // HttpClient avorte la requête, finalize éteint le spinner
    this.searchDropped = true;
  }

  // Une seule recherche en vol, par les filtres ou par l'id : la suivante, ou la moindre saisie, la coupe.
  private runSearch(filter: ProductFilter, onItems?: (items: any) => void): void {
    this.productsSearchSub?.unsubscribe(); // une réponse plus lente de la recherche d'avant écraserait celle-ci
    this.searchDropped = false;
    this.searchLaunched = true;
    this.loadingProducts = true;
    this.productsTpl = [];
    this.lastProductIdSearched = null; // la liste ne vient plus du raccourci id
    this.productsSearchSub = this.productsService.getProductsTplByFilter(filter).pipe(
      finalize(() => this.loadingProducts = false), // aussi sur erreur, sur le EMPTY d'un filtre vide et sur une coupure
    ).subscribe({
      next: (items: any) => {
        //this.formGroup.setControl('items', items);
        // items = items.slice(0, 5);  // FOR DEV
        this.productsTpl = items ?? [];
        onItems?.(items);

        // // FOR DEV
        // setTimeout(() => {
        //   // const el = document.getElementById("product_10978");  // FOR DEV
        //   const el = document.getElementById("product_10976");  // FOR DEV
        //   // const el = document.getElementById("product_10981");  // FOR DEV
        //   el?.click();
        // }, 150); // end FOR DEV
      },
      error: () => this.productsTpl = [],
    });
  }

  // Toujours bornée par « Nb rép. » : sans critère, elle rend les premiers articles des filtres Vendable/Achetable.
  onSearch(): void {
    const { name, ean, refsup, manuf, minprice, maxprice, is_sold, is_purchased, limit } = this.formGroup.value;

    console.log('this.formGroup', this.formGroup.value);
    console.log('this.icuf', this.icuf.value);
    // console.log('this.searchmanuf=', JSON.stringify( this.searchmanuf ) , this.searchmanuf  );

    const cufs: Record<string, string> = Object.fromEntries(Object.entries(this.icuf.value).filter(([, v]) => v !== null && v !== '' && v !== undefined)) as Record<string, string>;
    const filter: ProductFilter = {
      ...(name ? { name } : {}),
      ...(ean?.trim() ? { ean } : {}),
      ...(refsup?.trim() ? { refsup } : {}),
      ...(manuf ? { manuf } : {}),
      ...(minprice != null ? { minprice } : {}),
      ...(maxprice != null ? { maxprice } : {}),
      ...(is_sold != null ? { is_sold } : {}),
      ...(is_purchased != null ? { is_purchased } : {}),
      ...(limit ? { limit } : {}),
      ...(Object.keys(cufs).length ? { cufs } : {}),
    };
    this.runSearch(filter);
  }

  manufactureChange(newManufacture: Partner) {
    console.log('newManufacture', newManufacture);
    // this.formGroup.get("lnk_partner_manuf")?.patchValue(common.Partner.asFormGroup(newManufacture));
    if (newManufacture && newManufacture.id) {
      // If a manufacture is selected
      this.searchmanuf = newManufacture;
      this.formGroup.get('manuf')?.setValue(newManufacture.id);
    } else {
      this.searchmanuf = new Partner();
      this.formGroup.get('manuf')?.setValue(null);
    }
  }

  // Vendable / Achetable : Oui → Non → Tous ; la liste suit aussitôt, comme pour « Nb rép. ».
  cycleFlag(ctrl: 'is_sold' | 'is_purchased'): void {
    const v: boolean | null = this.formGroup.get(ctrl)?.value ?? null;
    this.formGroup.get(ctrl)?.setValue(v === true ? false : v === false ? null : true);
    this.onSearch();
  }

  flagIcon(v: boolean | null): string {
    return v === true ? 'check_box' : v === false ? 'check_box_outline_blank' : 'indeterminate_check_box';
  }

  flagText(v: boolean | null): string {
    return v === true ? 'Oui' : v === false ? 'Non' : 'Tous';
  }

  onCancel(): void {
    this.formGroup.reset({
      name: '',
      ean: '',
      refsup: '',
      manuf: null,
      minprice: null,
      maxprice: null,
      is_sold: true,
      is_purchased: null,
      limit: constants.LIMIT_SELECTOR,
    });
    this.icuf.reset();
    this.searchmanuf = new Partner();
    this.productIdNavInput?.clear(); // le raccourci id fait partie des filtres, il se vide aussi
    this.lastProductIdSearched = null;
    this.productsSearchSub?.unsubscribe(); // retour à l'état d'arrivée : liste vide, aucune requête
    this.productsTpl = [];
    this.searchDropped = false;
    this.searchLaunched = false;
  }

  onClickRow(row: any, e: MouseEvent): void {
    // if we are not in dialog mode we navigate with the link router inside the <a> balise because better for web broweser.
    // but if we are in dialog mode we  open a second dialog to select packaging and qty and  after return the previous page with the product.
    if (!this.dialogRef) return; // If we are not in dialog mode we do nothing because the <a> balise with routerLink will do the job

    // Dangerous we have 3 cases
    // row can be a product template with variants => we do nothing
    // row can be a product template without variant => we return the first variant linked (flat product)
    // row can be a product variant => we return the variant productTpl whit declinaison and we have click on the variant row

    // Même critère que la classe CSS template_product (curseur not-allowed) : un modèle n'est
    // sélectionnable que s'il affiche exactement une déclinaison. has_variants (= le modèle a des
    // attributs en base) ne convient pas : un modèle repris peut porter n déclinaisons sans attribut,
    // et une recherche par id (ou par cuf) ne ramène qu'une déclinaison, la ligne du modèle est alors
    // le seul choix offert.
    const isTemplateRow = Array.isArray(row.lnk_products);
    const uniqueDisplayedVariant = isTemplateRow && row.lnk_products.length === 1 ? row.lnk_products[0] : null;

    if (uniqueDisplayedVariant) {
      // Modèle à déclinaison unique => on retourne cette déclinaison
      this.openSelectPackagingQtyDialog(uniqueDisplayedVariant, this.dialogRef);
    } else if (!isTemplateRow && row.template) {
      // Ligne de déclinaison (produit spécifique) cliquée directement
      this.openSelectPackagingQtyDialog(row, this.dialogRef);
    } else {
      // Modèle à 0 ou plusieurs déclinaisons affichées : jamais ajoutable, l'utilisateur clique la bonne déclinaison
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // console.log('this.dialogRef',this.dialogRef);

    // this.dialogRef.close(row.id);

    // if (row.has_variants || row.lnk_products.length === 0) {	// This is a template OR orphan template (no variant linked)
    // 	if (!this.dialogRef) this.router.navigate(['/sales/product-tpl/' + row.id]);	// Jump to template only on standalone mode (NOT dialog)
    // 	this.dialogRef.close(row.id);
    // } else {	// Template with unique variant (or product variant) => jump to variant
    // 	let template = row.lnk_products[0].template;
    // 	if (this.dialogRef) {	// Dialog mode, return custom object
    // 		// let ret = {product:row.lnk_products[0].id, product_name:row.lnk_products[0].name, template:template, price_taxi:row.lnk_products[0].price_taxi}
    // 		//let ret = row.lnk_products[0].id;						// Return product id
    // 		this.dialogRef.close(row.id);
    // 	} else {
    // 		this.router.navigate(['/sales/variant/' + template + '/' + row.id]);
    // 	}
    // }
  }

  openSelectPackagingQtyDialog(product: Product, dialogRef: MatDialogRef<ProductsComponent>) {
    console.log('Opening dialog of packaging and qty,  for the product : ', product);
    const SelectPackagingQtyDialogRef = this.dialog.open(SelectPackagingQty, { width: '60%', data: product });

    SelectPackagingQtyDialogRef.afterClosed().subscribe(resultSelectPackagingAndQty => {
      console.log(' salut from afterClosed select packaging and qty dialog');

      // this.dialogRef.close();  // we close the product dialog
      if (resultSelectPackagingAndQty) {
        dialogRef.close(resultSelectPackagingAndQty);
      }
    });
  }

  // --- Raccourci « Id produit » -------------------------------------------------------------
  // Tab affiche l'article dans la liste, le lien ouvre sa fiche dans un nouvel onglet ; Entrée ouvre
  // la fiche en pleine page, et en modale renvoie l'article à l'appelant via la saisie du conditionnement.

  private parseProductId(value: string): number | null {
    const id = Number((value ?? '').toString().trim());
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  onProductIdChange(value: string): void {
    this.dropPendingSearch(); // champ hors formGroup : sa frappe ne passe pas par valueChanges
    const id = this.parseProductId(value);
    this.productIdHref = id ? this.router.serializeUrl(this.router.createUrlTree(['/sales/product', id])) : null;
  }

  onProductIdBlur(value: string): void {
    if (this.productIdNavClosing) return; // la modale se referme, la requête n'aurait plus de destinataire
    const id = this.parseProductId(value);
    if (!id || id === this.lastProductIdSearched) return;
    this.searchProductById(id);
  }

  onProductIdLinkClick(): void {
    if (!this.productIdHref || !this.dialogRef) return; // en pleine page le navigateur fait tout le travail
    this.productIdNavClosing = true;
    // On laisse le navigateur exécuter l'action par défaut du lien (ouverture de l'onglet) avant de
    // retirer la modale : fermer en plein dispatch du click peut annuler la navigation.
    setTimeout(() => this.dialogRef.close());
  }

  onProductIdValidate(value: string): void {
    const id = this.parseProductId(value);
    if (!id) return;
    if (!this.dialogRef) {
      this.productIdNavClosing = true; // le blur que peut lancer la destruction de l'écran ne doit plus chercher
      this.router.navigate(['/sales/product', id]); // pleine page : droit à la fiche, sans requête pour une seule ligne
      return;
    }
    if (id === this.lastProductIdSearched) {
      this.pickSearchedProduct(id);
      return;
    }
    this.searchProductById(id, () => this.pickSearchedProduct(id));
  }

  // Renvoie à l'appelant l'article affiché, en passant par la saisie conditionnement + quantité.
  private pickSearchedProduct(id: number): void {
    const product = this.productsTpl?.[0]?.lnk_products?.[0];
    if (!product || Number(product.id) !== id || !this.dialogRef) return; // id inconnu : la liste vide fait déjà le message
    this.openSelectPackagingQtyDialog(product, this.dialogRef);
  }

  // La recherche par id est exclusive côté serveur (product_id écrase tous les autres critères, cf.
  // ProductsService.getProductsTplByFilter) : on vide les autres champs pour que l'écran montre
  // bien ce qui est interrogé. Le serveur ne renvoie que la déclinaison demandée => une seule ligne.
  private searchProductById(id: number, onFound?: () => void): void {
    this.formGroup.patchValue({ name: '', ean: '', refsup: '', manuf: null, minprice: null, maxprice: null, is_sold: null, is_purchased: null }); // l'id ignore Vendable/Achetable : « Tous » le dit
    this.icuf.reset();
    this.icuf.markAsPristine();
    this.searchmanuf = new Partner();

    // En échec ou coupée par une saisie, la recherche ne passe pas pour « déjà cherchée » : runSearch a remis lastProductIdSearched à null.
    this.runSearch({ product_id: id }, (items: any) => {
      this.lastProductIdSearched = items ? id : null;
      onFound?.();
    });
  }

  isConfigurableProduct(product: any): boolean {
    if (product.data && product.data.cufs && product.data.cufs.hasOwnProperty(constants.C_CUF_CONFIGURATION)) {
      return true;
    }
    return false;
  }

  // Premier clic (ou Entrée) : croissant ; les suivants inversent le sens.
  onSortClick(col: [string, string, string, boolean?, string?]): void {
    this.sortProductTpl(col[3] !== true, col[0]);
  }

  sortProductTpl(order: boolean, name: string) {
    // console.log('sortProductTpl',order,name);
    // first we make sort on lnk_products the children if there are some because i don't know and uderstand why we don't have sometimes
    this.productsTpl = this.productsTpl.map((productTpl: any) => {
      // console.log('productTpl=', JSON.stringify( productTpl ) , productTpl  );
      // if ( productTpl.lnk_products && productTpl.lnk_products.length > 0) {
      productTpl.lnk_products = productTpl.lnk_products.sort((a: any, b: any) => {
        let caracteristic_name = name;
        if (name === 'lnk_partner_manuf.name') {
          caracteristic_name = 'attributestring';
        }
        let valA = this.getNestedProperty(a, caracteristic_name);
        let valB = this.getNestedProperty(b, caracteristic_name);
        if (name === 'refs') {
          valA = this.getNestedProperty(a, 'partnbloc') + this.getNestedProperty(a, 'partnbsup') + this.getNestedProperty(a, 'partnman');
          valB = this.getNestedProperty(b, 'partnbloc') + this.getNestedProperty(b, 'partnbsup') + this.getNestedProperty(b, 'partnman');
        }
        const isNumeric = (v: any) => !isNaN(parseFloat(v)) && isFinite(v);

        if (isNumeric(valA) && isNumeric(valB)) {
          return order ? parseFloat(valA) - parseFloat(valB) : parseFloat(valB) - parseFloat(valA);
        } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
          return order ? Number(valB) - Number(valA) : Number(valA) - Number(valB);
        } else {
          return order ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
        }
      });
      //  console.log('name=', JSON.stringify( name ) , name  );

      if (!(name in productTpl) && productTpl.lnk_products && productTpl.lnk_products.length > 0) {
        // if name is not in productTpl we add it
        if (name === 'refs') {
          productTpl[name] = productTpl.lnk_products[0].partnbloc + productTpl.lnk_products[0].partnbsup + productTpl.lnk_products[0].partnman;
        } else {
          productTpl[name] = productTpl.lnk_products[0][name];
        }
      }
      return productTpl;
      // }
    });
    // secondly we make sort on productTpl itself
    this.productsTpl = this.productsTpl.sort((a: any, b: any) => {
      let valA = this.getNestedProperty(a, name);
      let valB = this.getNestedProperty(b, name);
      if (name.startsWith('lnk_cufs')) {
        if (valA == null && a.lnk_products?.length > 0) valA = this.getNestedProperty(a.lnk_products[0], name);
        if (valB == null && b.lnk_products?.length > 0) valB = this.getNestedProperty(b.lnk_products[0], name);
      }
      if (name === 'baseuom/unitpriceuom') {
        valA = this.getNestedProperty(a, 'baseuom') + this.getNestedProperty(a, 'unitpriceuom');
        valB = this.getNestedProperty(b, 'baseuom') + this.getNestedProperty(b, 'unitpriceuom');
      }
      const isNumeric = (v: any) => !isNaN(parseFloat(v)) && isFinite(v);
      if (name === 'is_sold' || name === 'is_purchased') {
        // permet de mettre les articles complex (généric + spécific en tête  ) indépendament du reste quand on clic sur is_sold ou is_purchased
        // if (      a.lnk_products.length === 1 && b.lnk_products.length === 1 ) { return 0; }
        if (a.lnk_products.length === 1 && b.lnk_products.length !== 1) {
          return Number(order);
        } else if (b.lnk_products.length === 1 && a.lnk_products.length !== 1) {
          return Number(!order);
        }
        // return order ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
      }

      if (isNumeric(valA) && isNumeric(valB)) {
        return order ? parseFloat(valA) - parseFloat(valB) : parseFloat(valB) - parseFloat(valA);
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return order ? Number(valB) - Number(valA) : Number(valA) - Number(valB);
      } else {
        return order ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
      }
    });
    // col[4] est l'infobulle d'entête : la reconduire, sinon le premier tri fait disparaître
    // l'explication des colonnes « V. », « A. » et « # ».
    this.displayedColumnsInfo = this.displayedColumnsInfo.map(col => (col[0] === name ? [col[0], col[1], col[2], order, col[4]] : [col[0], col[1], col[2], undefined, col[4]]));
  }

  getNestedProperty(obj: any, path: string): any {
    if (!obj || !path) return null;
    // console.log('obj',obj);
    // console.log('path',path);
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  onMinPriceChange() {
    const minPrice = this.formGroup.get('minprice')?.value;
    const maxPrice = this.formGroup.get('maxprice')?.value;
    if (maxPrice != null && minPrice != null && minPrice > maxPrice) {
      this.formGroup.get('maxprice')?.setValue(minPrice);
      setTimeout(() => {
        this.maxPriceInput?.nativeElement?.focus();
        this.maxPriceInput?.nativeElement?.select();
      });
    }
  }

  onMaxPriceChange() {
    const minPrice = this.formGroup.get('minprice')?.value;
    const maxPrice = this.formGroup.get('maxprice')?.value;
    if (minPrice != null && maxPrice != null && maxPrice < minPrice) {
      this.formGroup.get('minprice')?.setValue(maxPrice);
      setTimeout(() => {
        this.minPriceInput?.nativeElement?.focus();
        this.minPriceInput?.nativeElement?.select();
      });
    }
  }
}
